<!-- Copyright (c) 2009-2021. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <div class="panel uploaded-files-list" :class="{embedded}">
    <p class="panel-heading" v-if="!embedded">
      {{ $t('storage') }}
    </p>
    <div class="panel-block storage">
      <div class="uf-toolbar">
        <div class="uf-search">
          <b-input
            :value="searchString"
            @input="debounceSearchString"
            class="search-uploaded-file"
            :placeholder="$t('search-by-filename')"
            icon="search"
          />
        </div>
        <div class="uf-count" v-if="localTotal !== null">
          {{ localTotal }} {{ $tc('count-images', localTotal) }}
        </div>
      </div>

      <ims-table
        :collection="uploadedFileCollection"
        sort="created" order="desc"
        :perPage="10"
        :revision="revision"
        :refreshInterval="tableRefreshInterval"
        :openedDetailed.sync="openedDetails"
        :detailed="false"
        @setCollectionSize="onSize"
      >
        <template #default="{row: uFile}">
          <b-table-column :label="$t('preview')" width="96" class="image-overview">
            <!-- slide-card with a vertical label strip (design .thumb/.tlabel) -->
            <div v-if="uFile.thumbURL" class="thumb" :class="{clickable: !!uFile.image}" @click="openDetail(uFile)" :title="uFile.image ? $t('details') : ''">
              <span class="strip"><span>{{ uFile.originalFilename }}</span></span>
              <div class="pic">
                <image-thumbnail :url="uFile.thumbURL" :size="128" :key="uFile.thumbURL" :extra-parameters="{authorization: 'Bearer ' + shortTermToken }"/>
              </div>
            </div>
            <div v-else class="thumb thumb-empty"><i class="fas fa-image"></i></div>
          </b-table-column>

          <b-table-column field="originalFilename" :label="$t('filename')" sortable :data-filename="uFile.originalFilename">
            <a v-if="uFile.image" class="fname fname-link" :data-filename="uFile.originalFilename" @click="openDetail(uFile)">{{ uFile.originalFilename }}</a>
            <span v-else class="fname" :data-filename="uFile.originalFilename">{{ uFile.originalFilename }}</span>
          </b-table-column>

          <b-table-column field="created" :label="$t('created')" sortable width="150">
            <span class="cell-muted">{{ Number(uFile.created) | moment('lll') }}</span>
          </b-table-column>

          <b-table-column field="size" :label="$t('size')" sortable width="90">
            <span class="cell-size">{{ filesize(uFile.size) }}</span>
          </b-table-column>

          <b-table-column field="status" :label="$t('status')" sortable width="120">
            <span class="uf-status"><uploaded-file-status :file="uFile" /></span>
          </b-table-column>

          <b-table-column :label="$t('projects')" width="100" centered>
            <uploaded-file-projects-cell :u-file="uFile" :accessible-projects="accessibleProjects" @open="openProjectsModal" />
          </b-table-column>

          <b-table-column label="" width="110">
            <div class="uf-actions">
              <a
                class="uf-icon-btn uf-icon-blue"
                :title="$t('button-download')"
                @click="download(uFile.downloadURL)"
                v-if="uFile.status >= 100"
              >
                <i class="fas fa-download"></i>
              </a>
              <button
                class="uf-icon-btn uf-icon-red delete-file"
                :title="$t('button-delete')"
                :data-filename="uFile.originalFilename"
                @click="confirmDeletion(uFile)"
              >
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
          </b-table-column>

        </template>

        <template #empty>
          <div class="uf-empty">
            <div class="uf-empty-ico"><i class="fas fa-search"></i></div>
            <b>{{ $t('no-images-found') }}</b>
            <span>{{ searchString ? $t('nothing-matches-search') : $t('upload-slides-to-see') }}</span>
          </div>
        </template>
      </ims-table>
    </div>

    <storage-detail-modal
      :active.sync="detailActive"
      :image="detailImage"
      :loading="detailLoading"
      :file-name="detailFileName"
    />

    <uploaded-file-projects-modal
      :active.sync="projectsModalActive"
      :data="projectsModalData"
    />
  </div>
</template>

<script>
import {get} from '@/utils/store-helpers';
import ImageThumbnail from '@/components/image/ImageThumbnail';
import StorageDetailModal from './StorageDetailModal';
import UploadedFileProjectsCell from './UploadedFileProjectsCell';
import UploadedFileProjectsModal from './UploadedFileProjectsModal';
import {Ims, UploadedFileCollection, UploadedFile, AbstractImage, ImageInstance, ProjectCollection} from '@/api';
import filesize from 'filesize';
import _ from 'lodash';
import ImsTable from '@/components/utils/ImsTable';
import UploadedFileStatusComponent from './UploadedFileStatus';
import {appendShortTermToken} from '@/utils/token-utils';

export default {
  name: 'list-uploaded-files',
  components: {
    ImsTable,
    ImageThumbnail,
    StorageDetailModal,
    UploadedFileProjectsCell,
    UploadedFileProjectsModal,
    'uploaded-file-status': UploadedFileStatusComponent
  },
  data() {
    return {
      loading: true,
      searchString: '',
      openedDetails: [],
      localTotal: null,

      // storage detail popup
      detailActive: false,
      detailImage: null,
      detailLoading: false,
      detailFileName: '',
      detailToken: 0,

      // "Projects" column: id→name of projects the user can access (for names + links),
      // and the popup state.
      accessibleProjects: {},
      projectsModalActive: false,
      projectsModalData: null
    };
  },
  props: {
    tableRefreshInterval: {
      type: Number,
      default: 0
    },
    revision : {type: Number, default: 0},
    // embedded → rendered inside the Image-Library shell, which supplies its own
    // header, so drop this component's panel heading + border chrome.
    embedded: {type: Boolean, default: false}
  },
  computed: {
    currentUser: get('currentUser/user'),
    users: get('currentStorage/users'),
    shortTermToken: get('currentUser/shortTermToken'),
    uploadedFileCollection() {
      return new UploadedFileCollection({
        onlyRootsWithDetails: true,
        originalFilename: {ilike: encodeURIComponent(this.searchString)}
      });
    }
  },
  methods: {
    filesize(size) {
      return (size) ? filesize(size, {base: 10}) : null;
    },
    // keep the count for the toolbar and forward it up for the rail badge.
    onSize(total) {
      this.localTotal = total;
      this.$emit('total', total);
    },
    debounceSearchString: _.debounce(async function (value) {
      this.searchString = value;
    }, 500),
    updatedTree() {
      this.$emit('update:revision', this.revision + 1); // updating the table will result in new files objects => the uf details will also be updated
    },
    download(url) {
      window.location.assign(appendShortTermToken(url, this.shortTermToken));
    },
    // Open the read-only detail popup for a converted upload. Resolves the produced
    // AbstractImage and, if the slide is a project image instance, that instance (which
    // carries the project link + case-info). Case-info only exists on the instance.
    async openDetail(uFile) {
      if (!uFile.image) {
        return; // not converted yet → no image to show
      }
      this.detailToken++;
      const token = this.detailToken;
      this.detailFileName = uFile.originalFilename;
      this.detailImage = null;
      this.detailLoading = true;
      this.detailActive = true;

      let resolved = null;
      try {
        // prefer a project ImageInstance of this slide (has project + case-info)
        let instances = (await Ims.instance.api.get(`abstractimage/${uFile.image}/imageinstance.json`)).data.collection || [];
        if (instances.length) {
          resolved = await ImageInstance.fetch(instances[0].id);
        }
      } catch (error) {
        resolved = null; // no accessible instance → fall back to the abstract image
      }
      if (!resolved) {
        try {
          resolved = await AbstractImage.fetch(uFile.image);
        } catch (error) {
          console.log(error);
        }
      }
      if (token === this.detailToken) {
        this.detailImage = resolved;
        this.detailLoading = false;
      }
    },
    openProjectsModal(data) {
      this.projectsModalData = data;
      this.projectsModalActive = true;
    },
    async fetchAccessibleProjects() {
      // names + accessibility for the "Projects" column / popup links
      try {
        let projects = (await ProjectCollection.fetchAll({light: true})).array;
        let map = {};
        projects.forEach(p => {
          map[p.id] = p.name;
        });
        this.accessibleProjects = map;
      } catch (error) {
        console.log(error);
      }
    },
    confirmDeletion(uFile) {
      this.$buefy.dialog.confirm({
        title: this.$t('confirm-deletion'),
        message: this.$t('confirm-deletion-file'),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: () => this.deleteFile(uFile)
      });
    },
    async deleteFile(uFile) {
      try {
        await UploadedFile.delete(uFile.id);
        this.updatedTree();
      } catch (error) {
        console.log(error);
        let errorValues = error.response.data.errorValues;
        let text;
        if (error.response.status === 403) {
          if (errorValues && errorValues.projectNames && errorValues.imageNames) {
            text = this.$t('notif-error-delete-used-uploaded-file', {
              projects: errorValues.projectNames.join(', '),
              names: errorValues.imageNames.join(', ')
            });
          } else {
            text = this.$t('notif-error-delete-uploaded-file-forbidden');
          }
        } else {
          text = this.$t('notif-error-delete-uploaded-file');
        }
        this.$notify({type: 'error', text});
      }
    },
  },
  created() {
    this.fetchAccessibleProjects();
  },
};
</script>

<style scoped>
.panel-block.storage {
  min-height: 20vh;
  position: relative;
}

/* slide-card preview with a vertical label strip (design .thumb/.tlabel) */
.thumb {
  width: 78px;
  height: 54px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  border: 1px solid #e6eaf0;
  background: #f8fafc;
}
.thumb .strip {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.thumb .strip span {
  font-family: ui-monospace, monospace;
  font-size: 7px;
  color: #94a3b8;
  white-space: nowrap;
  transform: rotate(-90deg);
  letter-spacing: 0.02em;
}
.thumb .pic {
  position: absolute;
  left: 14px;
  right: 0;
  top: 0;
  bottom: 0;
}
.thumb .pic >>> img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.thumb-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd2dd;
}

/* toolbar: search + image count */
.uf-toolbar {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}
.uf-search { flex: 1; max-width: 380px; }
.uf-count {
  margin-left: auto;
  font-size: 13px;
  color: #64748b;
  white-space: nowrap;
}

/* empty state */
.uf-empty {
  padding: 56px 20px;
  text-align: center;
  color: #94a3b8;
}
.uf-empty-ico {
  width: 54px;
  height: 54px;
  border-radius: 14px;
  background: #f4f7fb;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin: 0 auto 14px;
}
.uf-empty b { display: block; color: #334155; font-size: 15px; margin-bottom: 4px; }
.uf-empty span { font-size: 13.5px; }

/* row actions: square icon-only buttons (blue download / red delete), same row + gap */
.uf-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}
.uf-icon-btn {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e6eaf0;
  background: #fff;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: 0.14s;
}
.uf-icon-btn .fas { padding-right: 0; }
.uf-icon-blue:hover { background: #eef4ff; border-color: #1f6fe5; color: #1f6fe5; }
.uf-icon-red:hover { background: #fef2f2; border-color: #ef4444; color: #dc2626; }

.thumb.clickable { cursor: pointer; }
.thumb.clickable:hover { border-color: #1f6fe5; box-shadow: 0 0 0 2px rgba(31, 111, 229, 0.18); }
.fname { font-weight: 600; color: #0f172a; font-size: 14px; }
.fname-link { font-weight: 600; color: #1f6fe5; font-size: 14px; cursor: pointer; }
.fname-link:hover { text-decoration: underline; }
.cell-muted { color: #64748b; font-size: 13.5px; }
.cell-size { font-variant-numeric: tabular-nums; color: #334155; }
</style>

<!-- non-scoped: style Buefy's generated table / pagination DOM, namespaced to the
     embedded storage list so nothing leaks to other tables. -->
<style lang="scss">
.uploaded-files-list.embedded {
  border: 0;
  border-radius: 0;
  box-shadow: none;
  background: transparent;

  .panel-block.storage { padding: 0; display: block; border: 0; }

  .search-uploaded-file { max-width: 380px; }
  .search-uploaded-file .control.has-icons-left .icon.is-left {
    height: 42px;
    width: 42px;
    color: #94a3b8;
  }
  .search-uploaded-file .input {
    height: 42px;
    border-radius: 10px;
    border-color: #e6eaf0;
    box-shadow: none;
    font-size: 14px;
    color: #0f172a;
    padding-left: 40px;
  }
  .search-uploaded-file .input::placeholder { color: #94a3b8; }
  .search-uploaded-file .input:hover { border-color: #cdd6e4; }
  .search-uploaded-file .input:focus {
    border-color: #1f6fe5;
    box-shadow: 0 0 0 3px rgba(31, 111, 229, 0.13);
  }

  /* table */
  .b-table, .ims-table-wrapper { width: 100%; }
  .table-wrapper { overflow-x: visible; margin-bottom: 0; }
  table.table { background: transparent; width: 100%; }
  table.table thead th {
    border-width: 0 0 1px;
    border-color: #e6eaf0;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #64748b;
    padding: 4px 14px 12px;
    vertical-align: middle;
  }
  table.table thead th .th-wrap { white-space: nowrap; }
  table.table thead th .icon { color: #94a3b8; }
  table.table tbody td {
    border-color: #eef1f6;
    padding: 14px;
    vertical-align: middle;
    font-size: 14px;
    color: #334155;
  }
  table.table tbody tr:hover { background: #fafbfd; }

  /* status badge → green/amber pill with a leading dot; file-count = plain muted */
  .uf-status .tags { flex-wrap: nowrap; align-items: center; }
  .uf-status .tag {
    border-radius: 7px;
    font-size: 12px;
    font-weight: 700;
    padding: 4px 10px;
    height: auto;
  }
  .uf-status .tag:not(.is-light)::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    margin-right: 6px;
    flex: 0 0 6px;
  }
  .uf-status .tag.is-success { background: #dcfce7; color: #15803d; }
  .uf-status .tag.is-info { background: #fef3c7; color: #b45309; }
  .uf-status .tag.is-danger { background: #fef2f2; color: #dc2626; }
  .uf-status .tags.has-addons .tag.is-light {
    background: transparent;
    color: #64748b;
    font-weight: 500;
    padding-left: 6px;
  }

  /* pagination → match design .pg buttons + per-page mini-select */
  .level { margin-top: 20px; padding-top: 16px; border-top: 1px solid #eef1f6; }
  .pagination-link,
  .pagination-previous,
  .pagination-next {
    border-radius: 9px;
    border-color: #e6eaf0;
    color: #334155;
    font-weight: 600;
    min-width: 36px;
    height: 36px;
  }
  .pagination-link:hover,
  .pagination-previous:hover,
  .pagination-next:hover { border-color: #cdd6e4; background: #f8fafc; }
  .pagination-link.is-current {
    background: #1f6fe5;
    border-color: #1f6fe5;
    color: #fff;
  }
  .select:not(.is-multiple) { height: 36px; }
  .select:not(.is-multiple):not(.is-loading)::after {
    border-color: #64748b;
    right: 1.1em;
  }
  .select select {
    height: 36px;
    border-radius: 9px;
    border-color: #e6eaf0;
    font-weight: 600;
    color: #334155;
  }
}
</style>

